import { Bell, MessageSquare, User, Settings, Shield, ShoppingCart, Package } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUserRole, UserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import { CompactSearchBar } from "@/components/search/CompactSearchBar";
import { KeyboardShortcutsHelp } from "@/components/navigation/KeyboardShortcutsHelp";
import { buyerNavConfig } from "@/config/navigation/buyerNav";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { NavProfileMenu } from "@/components/navigation/NavProfileMenu";
import { getDisplayName, getFallbackInitial, getDisplayImage } from "@/lib/utils";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useSessionQuery } from "@/hooks/queries/useSessionQuery";
import { logger } from "@/lib/logger";

export function BuyerTopNav() {
  const navigate = useNavigate();
  const { allRoles, role: currentRole, switchRole } = useUserRole();
  const { hasRole } = useAuth();
  const navContext = useNavigationContext();
  const { profile } = useUserProfile();
  const { data: session } = useSessionQuery();
  const isAdmin = hasRole('admin');

  // Use profile display name, with fallback to auth metadata when profile lacks full_name
  const displayName = useMemo(() => {
    const fromProfile = getDisplayName(profile, "buyer", "navigation");
    if (fromProfile !== "Account") return fromProfile;
    const meta = session?.user?.user_metadata;
    const metaName = meta?.full_name || meta?.name;
    return typeof metaName === "string" && metaName.trim() ? metaName.trim() : fromProfile;
  }, [profile, session?.user?.user_metadata]);

  const fallbackInitial = useMemo(() => {
    if (displayName !== "Account") return displayName.charAt(0).toUpperCase();
    return getFallbackInitial(profile, "buyer", "navigation");
  }, [profile, displayName]);
  const primaryCTA = buyerNavConfig.primaryCTA;

  const collaborationLinks = useMemo(
    () =>
      buyerNavConfig.sections
        .find((section) => section.id === "collaboration")
        ?.items.filter((item) => item.href !== "/buyer/messages") ?? [],
    []
  );

  // Fetch unread notifications
  const { data: notificationCount } = useQuery({
    queryKey: ['buyer-notifications'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from('notification_history')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) {
        logger.warn('Error fetching notifications count', { error });
        return 0;
      }

      return count || 0;
    },
    staleTime: 30 * 1000, // Cache for 30 seconds - consistent across all portals
    refetchInterval: 60 * 1000, // Refetch every minute - consistent across all portals
  });

  const messageCount = navContext.unreadMessages || 0;

  // Profile data comes from centralized useUserProfile hook

  const handleRoleSwitch = (newRole: UserRole) => {
    // Switch role first
    switchRole(newRole);
    toast.success(`Switched to ${newRole} portal`);

    // Use a longer delay and force navigation to ensure ProtectedRoute sees the updated state
    // The delay allows React to update all state and re-render components
    setTimeout(() => {
      // Force navigation with replace to prevent back button issues
      if (newRole === 'admin') {
        window.location.href = '/admin/dashboard';
      } else if (newRole === 'supplier') {
        window.location.href = '/supplier/dashboard';
      } else if (newRole === 'buyer') {
        window.location.href = '/buyer/dashboard';
      }
    }, 100);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
    navigate('/auth');
  };

  const profileActions = [
    {
      label: "My Profile",
      icon: User,
      onSelect: () => navigate("/buyer/profile"),
    },
    {
      label: "Settings",
      icon: Settings,
      onSelect: () => navigate("/buyer/settings"),
    },
  ];

  // Helper function to get portal icon
  const getRoleIcon = (role: UserRole) => {
    if (role === "admin") return Shield;
    if (role === "supplier") return Package;
    return ShoppingCart;
  };

  // Only show role switcher for admins (not for regular buyers/suppliers)
  const roleActions =
    isAdmin && allRoles.length > 1
      ? allRoles
        .filter((role) => role !== currentRole)
        .map((role) => ({
          label:
            role === "admin"
              ? "Admin Portal"
              : role === "supplier"
                ? "Supplier Portal"
                : "Buyer Portal",
          icon: getRoleIcon(role),
          onSelect: () => handleRoleSwitch(role),
        }))
      : undefined;

  const collaborationSections =
    collaborationLinks.length > 0
      ? [
        {
          label: "Collaboration & Support",
          items: collaborationLinks.map((item) => ({
            label: item.label,
            icon: item.icon,
            onSelect: () => navigate(item.href),
          })),
        },
      ]
      : undefined;

  return (
    <header className="flex h-16 min-w-0 items-center gap-2 overflow-hidden border-b border-border bg-white px-6 sm:gap-4">
      <div className="hidden shrink-0 items-center gap-2 sm:gap-4 md:flex">
        <Button size="sm" onClick={() => navigate(primaryCTA.href)} className="min-w-0 max-w-full overflow-hidden whitespace-nowrap">
          <primaryCTA.icon className="mr-2 size-4 shrink-0" />
          <span className="truncate">{primaryCTA.label}</span>
        </Button>
      </div>

      <div className="mx-8 min-w-0 max-w-xl flex-1">
        <CompactSearchBar
          placeholder="Search products, suppliers, RFQs..."
        />
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-6 overflow-visible">
        {/* Notifications */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="group relative size-11 overflow-visible text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => navigate('/buyer/notifications')}
                aria-label={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ''}`}
              >
                <Bell className="size-5" />
                {notificationCount > 0 && (
                  <Badge variant="default" className="absolute -right-1 -top-1 z-10 flex size-5 min-w-5 items-center justify-center p-0 text-xs font-semibold">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <div className="font-semibold">{notificationCount || 0} unread notifications</div>
                <div className="text-xs text-muted-foreground">Quotes, order updates, and messages</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Messages */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="group relative size-11 overflow-visible text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => navigate('/buyer/messages')}
                aria-label={`Messages${messageCount > 0 ? `, ${messageCount} unread` : ''}`}
              >
                <MessageSquare className="size-5" />
                {messageCount > 0 && (
                  <Badge variant="default" className="absolute -right-1 -top-1 z-10 flex size-5 min-w-5 items-center justify-center p-0 text-xs font-semibold">
                    {messageCount > 99 ? '99+' : messageCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <div className="font-semibold">{messageCount || 0} unread messages</div>
                <div className="text-xs text-muted-foreground">Conversations with suppliers</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <KeyboardShortcutsHelp />

        <NavProfileMenu
          name={displayName}
          email={profile?.email || session?.user?.email || undefined}
          avatarUrl={getDisplayImage(profile, 'buyer', 'navigation') || undefined}
          fallbackInitial={fallbackInitial}
          showNameInTrigger
          primaryActions={profileActions}
          sections={collaborationSections}
          roleActions={roleActions}
          onSignOut={handleSignOut}
        />
      </div>
    </header>
  );
}
